/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useState, useRef, useLayoutEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Button, Paper, IconButton, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useClickAway } from "hooks/useClickAway";
import WebpageEditContext from "components/webpage-selector/context";
import LabelledField from "components/labelled-field";
import ModalDialog from "components/webpage-selector/display/modal-dialog";
import { SVGIcons } from "cms/icons/svg-icons";
import WebpageCardIcon from "./display-card";
import { WebpageBannerImage } from "./banner-image";

const WebpageSelectorDisplayPanel = () => {
    
    const { t } = useTranslation();
    
    const webpageEditContext = useContext(WebpageEditContext);
    const onChange = webpageEditContext?.onChange;
    const onRemove = webpageEditContext?.onRemove;
    const selectedData = webpageEditContext?.selectedData;
    const model = webpageEditContext?.model;
    const webpage = webpageEditContext?.webpage;
    const changeTitle = webpageEditContext?.changeTitle;
    const changeDescription = webpageEditContext?.changeDescription;
    
    const image = selectedData?.image;
    
    const theme = useTheme();
    const buttonStyle = { color: theme.palette.primary.main };

    const wrapperRef = useRef();
    const infoButtonRef = useRef();
    const [location, setLocation] = useState({ top: 0, left: 0 });
    const [info, setInfo] = useState(false);
    
    const [title,setTitle] = useState(selectedData?.title);
    const [description,setDescription] = useState(selectedData?.description);
    
    const openInfo = async () => {
        setInfo(true);
    };
    const closeInfo = async () => {
        setInfo(false);
    };
    useClickAway(wrapperRef, closeInfo);

    useLayoutEffect(() => {
        if (infoButtonRef?.current) {
            setLocation({
                left: (infoButtonRef?.current?.offsetLeft || 0) - 10,
                top: (infoButtonRef?.current?.offsetTop || 0) + 30,
            });
        }
    }, [infoButtonRef]);
    
    // const onChangeImage = (event) => { }

    const onChangeTitle = (event) => {
        const newTitle = event.target.value;
        setTitle(newTitle);
        changeTitle(newTitle, model?.title);
    };

    const onChangeDescription = (event) => {
        const newDescription = event.target.value;
        setDescription(newDescription);
        changeDescription(newDescription, model?.description);
    };

    return (
        <Fragment>
            {webpage && webpage?.guid && (
                <div className='webpage-editor'>
                    <div className='webpage-editor-view'>
                        
                        {/* A card that represents the webpage */}
                        <WebpageCardIcon
                            webpage={webpage}
                        />
                        
                        <div className='image-display'>
                            <div className='webpage-actions' style={{ justifyContent: "space-between" }}>
                                <div className='image-info'>
                                    <IconButton
                                        ref={infoButtonRef}
                                        style={buttonStyle}
                                        aria-label='info'
                                        component='span'
                                        onClick={openInfo}
                                    >
                                        <InfoOutlinedIcon />
                                    </IconButton>
                                </div>
                                <div className='webpage-action-container'>
                                    <div className='image-action-change'>
                                        <Button onClick={onChange}>
                                            {t("commons.change")}
                                        </Button>
                                    </div>
                                    <div className='image-action-remove'>
                                        <Button onClick={onRemove}>
                                            <SVGIcons.common.Close />
                                            {t("commons.remove")}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            
                            {image &&
                                <WebpageBannerImage
                                    banner={image}
                                />
                            }
                            
                            <LabelledField label={t("commons.title")} style={{ marginTop: 12 }}>
                                <TextField
                                    id='webpage_title'
                                    fullWidth={true}
                                    value={title || model?.title}
                                    onChange={onChangeTitle}
                                />
                            </LabelledField>
                            <LabelledField label={t("commons.description")} style={{ marginTop: 12 }}>
                                <TextField
                                    id='webpage_url'
                                    fullWidth={true}
                                    multiline={true}
                                    maxRows={4}
                                    value={description || model?.description}
                                    onChange={onChangeDescription}
                                />
                            </LabelledField>
                            
                            
                            {/* From Webpage */}
                            <LabelledField label={t("commons.url")} style={{ marginTop: 12 }}>
                                <TextField
                                    id='webpage_url'
                                    className='disabled-field'
                                    fullWidth={true}
                                    disabled={true}
                                    value={webpage?.path}
                                />
                            </LabelledField>
                            <div ref={wrapperRef}>
                                {info &&
                                    <ModalDialog
                                        location={location}
                                        zIndex={30}
                                        aria-labelledby='modal-modal-title'
                                        aria-describedby='modal-modal-description'
                                    >
                                        <Paper elevation={3} className='image-info-box'>
                                            {/*<LabelledField label='Layout' variant='subtitle1'>*/}
                                            {/*    <span className='info-text'>{webpage.type}</span>*/}
                                            {/*</LabelledField>*/}
                                            <LabelledField label='Title' variant='subtitle1'>
                                                <span className='info-text'>{webpage.nav_title}</span>
                                            </LabelledField>
                                            <LabelledField label='Description' variant='subtitle1'>
                                                <span className='info-text'>{webpage.description}</span>
                                            </LabelledField>
                                        </Paper>
                                    </ModalDialog>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export { WebpageSelectorDisplayPanel };
